import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/components/ImageUploader";
import {
    MAKES,
    MODELS_BY_MAKE,
    PERFORMANCE_PACKAGES,
    ENGINES,
    DRIVETRAINS,
    TRANSMISSIONS,
    MODS_LEVELS,
    SELLER_TYPES,
    CURRENT_YEAR,
} from "@/lib/constants";

const initial = {
    year: CURRENT_YEAR,
    make: "Dodge",
    model_name: "Challenger",
    trim: "SRT Hellcat",
    engine: "6.2L Supercharged HEMI V8",
    drivetrain: "RWD",
    transmission: "Automatic",
    mods_level: "Stock",
    mods: [],
    price: "",
    mileage: "",
    location: "",
    seller_type: "private",
    description: "",
    photos: [],
};

export default function CreateListing() {
    const [f, setF] = useState(initial);
    const [modInput, setModInput] = useState("");
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();

    function update(k, v) {
        setF((prev) => ({ ...prev, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        if (!f.photos.length) {
            toast.error("Add at least 1 photo");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                ...f,
                year: Number(f.year),
                price: Number(f.price),
                mileage: Number(f.mileage),
            };
            const { data } = await api.post("/listings", payload);
            toast.success("Listing posted");
            navigate(`/listings/${data.listing_id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    }

    const models = MODELS_BY_MAKE[f.make] || [];

    return (
        <div className="min-h-screen bg-onyx-900">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg">Create Listing</div>
                </div>
            </div>

            <form onSubmit={submit} className="max-w-3xl mx-auto px-4 py-6 space-y-5">
                {/* Photos */}
                <Section title="Photos" hint="Add up to 20 photos. First photo is the hero.">
                    <ImageUploader
                        value={f.photos}
                        onChange={(v) => update("photos", v)}
                        max={20}
                        testid="listing-uploader"
                    />
                </Section>

                {/* Vehicle */}
                <Section title="Vehicle">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Year">
                            <input
                                data-testid="listing-year"
                                type="number"
                                min={1970}
                                max={CURRENT_YEAR + 1}
                                required
                                value={f.year}
                                onChange={(e) => update("year", e.target.value)}
                                className={inputCls}
                                placeholder={String(CURRENT_YEAR)}
                            />
                        </Field>
                        <Field label="Make">
                            <select
                                data-testid="listing-make"
                                value={f.make}
                                onChange={(e) =>
                                    setF((p) => ({ ...p, make: e.target.value, model_name: MODELS_BY_MAKE[e.target.value][0] }))
                                }
                                className={inputCls}
                            >
                                {MAKES.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Model">
                            <select
                                data-testid="listing-model"
                                value={f.model_name}
                                onChange={(e) => update("model_name", e.target.value)}
                                className={inputCls}
                            >
                                {models.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Package / Trim">
                            <select
                                data-testid="listing-trim"
                                value={f.trim}
                                onChange={(e) => update("trim", e.target.value)}
                                className={inputCls}
                            >
                                {PERFORMANCE_PACKAGES.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Engine">
                            <select
                                data-testid="listing-engine"
                                value={f.engine}
                                onChange={(e) => update("engine", e.target.value)}
                                className={inputCls}
                            >
                                {ENGINES.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Drivetrain">
                            <select
                                data-testid="listing-drivetrain"
                                value={f.drivetrain}
                                onChange={(e) => update("drivetrain", e.target.value)}
                                className={inputCls}
                            >
                                {DRIVETRAINS.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Transmission">
                            <select
                                data-testid="listing-transmission"
                                value={f.transmission}
                                onChange={(e) => update("transmission", e.target.value)}
                                className={inputCls}
                            >
                                {TRANSMISSIONS.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Mods level">
                            <select
                                data-testid="listing-mods-level"
                                value={f.mods_level}
                                onChange={(e) => update("mods_level", e.target.value)}
                                className={inputCls}
                            >
                                {MODS_LEVELS.map((m) => (
                                    <option key={m} value={m}>
                                        {m}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                </Section>

                {/* Price + location */}
                <Section title="Price & location">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Price ($)">
                            <input
                                data-testid="listing-price"
                                type="number"
                                required
                                min={1000}
                                value={f.price}
                                onChange={(e) => update("price", e.target.value)}
                                className={inputCls}
                                placeholder="55000"
                            />
                        </Field>
                        <Field label="Mileage">
                            <input
                                data-testid="listing-mileage"
                                type="number"
                                required
                                min={0}
                                value={f.mileage}
                                onChange={(e) => update("mileage", e.target.value)}
                                className={inputCls}
                                placeholder="12500"
                            />
                        </Field>
                        <Field label="Location" className="col-span-2">
                            <input
                                data-testid="listing-location"
                                required
                                value={f.location}
                                onChange={(e) => update("location", e.target.value)}
                                className={inputCls}
                                placeholder="Dallas, TX"
                            />
                        </Field>
                        <Field label="Seller type" className="col-span-2">
                            <select
                                data-testid="listing-seller-type"
                                value={f.seller_type}
                                onChange={(e) => update("seller_type", e.target.value)}
                                className={inputCls}
                            >
                                {SELLER_TYPES.map((s) => (
                                    <option key={s.value} value={s.value}>
                                        {s.label}
                                    </option>
                                ))}
                            </select>
                        </Field>
                    </div>
                </Section>

                <Section title="Mods list" hint="One at a time — press Enter or Add.">
                    <div className="flex gap-2 flex-wrap mb-2">
                        {f.mods.map((m, i) => (
                            <span
                                key={i}
                                className="surface-elevated rounded-sm px-3 py-1 text-sm inline-flex items-center gap-2"
                            >
                                {m}
                                <button
                                    type="button"
                                    onClick={() =>
                                        update(
                                            "mods",
                                            f.mods.filter((_, idx) => idx !== i),
                                        )
                                    }
                                    className="text-hellcat"
                                    data-testid={`remove-mod-${i}`}
                                >
                                    ×
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <input
                            data-testid="listing-mod-input"
                            value={modInput}
                            onChange={(e) => setModInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (modInput.trim()) {
                                        update("mods", [...f.mods, modInput.trim()]);
                                        setModInput("");
                                    }
                                }
                            }}
                            className={`${inputCls} flex-1`}
                            placeholder="e.g. Cat-back exhaust"
                        />
                        <button
                            type="button"
                            data-testid="listing-add-mod"
                            onClick={() => {
                                if (modInput.trim()) {
                                    update("mods", [...f.mods, modInput.trim()]);
                                    setModInput("");
                                }
                            }}
                            className="btn-ghost rounded-sm px-4 text-sm font-bold uppercase tracking-widest"
                        >
                            Add
                        </button>
                    </div>
                </Section>

                <Section title="Description">
                    <textarea
                        data-testid="listing-description"
                        value={f.description}
                        onChange={(e) => update("description", e.target.value)}
                        className={`${inputCls} h-32`}
                        placeholder="Tell buyers about the car, service history, negotiability, trades..."
                    />
                </Section>

                <button
                    type="submit"
                    disabled={busy}
                    data-testid="listing-submit"
                    className="btn-red w-full py-3.5 rounded-sm disabled:opacity-60"
                >
                    {busy ? "Posting..." : "Post listing"}
                </button>
            </form>
        </div>
    );
}

const inputCls =
    "w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat";

function Section({ title, hint, children }) {
    return (
        <div className="surface rounded-md p-5">
            <div className="mb-3">
                <div className="headline text-lg">{title}</div>
                {hint && <div className="text-xs text-onyx-500 mt-1">{hint}</div>}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`flex flex-col gap-1.5 ${className}`}>
            <span className="label-caps">{label}</span>
            {children}
        </label>
    );
}
